import React, { useState, useCallback, useRef, useEffect } from "react";
import { useHistory } from 'react-router-dom';
import { connect } from "react-redux";
import { Button } from 'devextreme-react/button';
import { TabPanel, Item as TabItem } from 'devextreme-react/tab-panel';
import { LoadPanel } from 'devextreme-react/load-panel';
import Toolbar, { Item } from "devextreme-react/toolbar";
import PrimaryTab from "./tabs/PrimaryTab";
import axios from "axios";
import data from "../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';
import { confirm } from 'devextreme/ui/dialog';
import { CONCURRENCY_HANDLED, getApiErrorMessage } from "../../../../common/concurrency";
import { MESSAGES } from "../../../../common/Messages";
import {
    EMPTY_GUID,
    ChargeProfileRoutes,
    ChargeCodeRoutes,
    RECORD_TYPE,
} from "../../../../const/constants";

// Add / Edit for the Delphi screen uWMD0008 "WM Charge Codes Profile Entry" (WMCHARGESDP).
//
// Structure follows itemDataProfile/Form.js: ALL-CAPS formFields keys (the dynamic-form convention),
// a PascalCase payload because the controller opts into DefaultContractResolver, PUT on update and
// POST on insert, and rule 16's ConcurrencyToken held in state and replaced from the save response.
// The conflict dialog comes free from the axios interceptor in AppContainer.js.
const ENTITY_LABEL = 'Charge Profile';

function Form(props) {
    const history = useHistory();
    const formRefs = useRef({});

    const initialFormFields = {
        ROWID: EMPTY_GUID,
        RECORD: '',
        // ROWID of this profile's RECORDTYPE='P' charge record, or empty when it has none yet. Comes
        // back with the record, so the Charges Defaults button knows without a lookup of its own.
        CHARGEDEFAULTSROWID: '',
        // R_CHARGES is persisted by the Delphi screen but has no control on it. Carried here (not
        // rendered) so a save echoes back what it loaded instead of blanking the column.
        RCHARGES: null,
        PROFILECODE: '',
        DESCRIPTION: '',
        COMMENT: '',
        USERDEFINEDCODE1: '',
        USERDEFINEDCODE2: '',
        USERDEFINEDCODE3: '',
        USERDEFINEDCODE4: '',
        USERDEFINEDCODE5: '',
        // Opaque optimistic-concurrency token. Loaded with the record, echoed back on save, and
        // replaced with the token the save returns. Never displayed or parsed.
        CONCURRENCYTOKEN: null,
    };

    const [formFields, setformFields] = useState(initialFormFields);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const accessToken = props?.oidc?.user?.access_token;

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    const position = { of: '#accordion' };

    const isSaved = formFields.ROWID && formFields.ROWID !== EMPTY_GUID;

    const handleFieldChange = useCallback((name, value) => {
        setformFields(prevFields => ({
            ...prevFields,
            [name]: value
        }));
    }, []);

    const mapResponseToFormFields = useCallback((item) => ({
        ROWID: item.RowId || EMPTY_GUID,
        RECORD: item.RecordNumber || '',
        CHARGEDEFAULTSROWID: item.ChargeDefaultsRowId || '',
        RCHARGES: item.RCharges ?? null,
        PROFILECODE: item.ProfileCode || '',
        DESCRIPTION: item.Description || '',
        COMMENT: item.Comment || '',
        USERDEFINEDCODE1: item.UserDefinedCode1 || '',
        USERDEFINEDCODE2: item.UserDefinedCode2 || '',
        USERDEFINEDCODE3: item.UserDefinedCode3 || '',
        USERDEFINEDCODE4: item.UserDefinedCode4 || '',
        USERDEFINEDCODE5: item.UserDefinedCode5 || '',
        CONCURRENCYTOKEN: item.ConcurrencyToken || null,
    }), []);

    const loadChargeProfileById = useCallback(async (rowId) => {
        if (!rowId || rowId === EMPTY_GUID) {
            return;
        }

        try {
            setLoadPanelVisible(true);
            const url = `${window._env_.REACT_APP_APIS}/${data.ChargeDp.GetChargeDp}?rowid=${rowId}`;
            const response = await axios.get(url, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`
                }
            });

            if (response.data) {
                setformFields(mapResponseToFormFields(response.data));
            }
        } catch (error) {
            console.error('Error loading Charge Profile:', error);
            notify(getApiErrorMessage(error, MESSAGES.FETCH_ERROR), 'error', 3000);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [accessToken, mapResponseToFormFields]);

    useEffect(() => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadChargeProfileById(rowId);
        }
    }, [props.id, loadChargeProfileById]);

    const handleFormSubmit = async (e) => {
        e.preventDefault();
        setLoadPanelVisible(true);

        try {
            const isUpdate = !!isSaved;
            const payload = {
                RowId: isUpdate ? formFields.ROWID : EMPTY_GUID,
                KeySequence: 0,
                ProfileCode: formFields.PROFILECODE || "",
                Description: formFields.DESCRIPTION || "",
                Comment: formFields.COMMENT || "",
                // Echoed back unchanged so the save does not blank the column this screen cannot show.
                RCharges: formFields.RCHARGES ?? null,
                UserDefinedCode1: formFields.USERDEFINEDCODE1 || "",
                UserDefinedCode2: formFields.USERDEFINEDCODE2 || "",
                UserDefinedCode3: formFields.USERDEFINEDCODE3 || "",
                UserDefinedCode4: formFields.USERDEFINEDCODE4 || "",
                UserDefinedCode5: formFields.USERDEFINEDCODE5 || "",
                // Tells the API which version of the row this edit started from. Null on insert.
                ConcurrencyToken: isUpdate ? (formFields.CONCURRENCYTOKEN || null) : null,
            };

            const endpoint = isUpdate
                ? data.ChargeDp.UpdateChargeDp
                : data.ChargeDp.InsertChargeDp;
            const method = isUpdate ? 'put' : 'post';

            const url = `${window._env_.REACT_APP_APIS}/${endpoint}`;
            const resp = await axios[method](url, payload, {
                headers: {
                    access_token: accessToken,
                    Authorization: `Bearer ${accessToken}`,
                }
            });

            const saved = resp?.data;
            if (saved) {
                // Adopt the saved row, including a token for the version just written. Without that the
                // next save would still echo the token the page was opened with and falsely conflict.
                setformFields(mapResponseToFormFields(saved));
            }

            // The service already distinguishes the two (Messages.EntityInsertedSuccessfullyFormat
            // vs EntityUpdatedSuccessfullyFormat), so prefer what it sent; the local constants are
            // only a fallback, and they have to branch too or an insert reads as an update.
            notify(saved?.Message || (isUpdate ? MESSAGES.UPDATE_SUCCESS : MESSAGES.CREATE_SUCCESS), "success", 3000);
        } catch (err) {
            // A stale save is already handled globally by the axios interceptor, which raises the
            // "record changed - reload" dialog. Don't stack a red toast on top of it.
            if (err?.[CONCURRENCY_HANDLED]) {
                return;
            }

            notify(getApiErrorMessage(err, MESSAGES.ERROR), "error", 5000);
        } finally {
            hideLoadPanel();
        }
    };

    const DeleteClick = () => {
        // Defensive only - the Delete button is disabled until the record exists.
        if (!isSaved) {
            return;
        }

        // Rule 12: project-standard confirmation wording.
        let result = confirm(MESSAGES.DELETE_SELECTED_RECORD_CONFIRM, MESSAGES.CONFIRM);
        result.then(async (dialogResult) => {
            if (dialogResult) {
                setLoadPanelVisible(true);
                try {
                    const url = `${window._env_.REACT_APP_APIS}/${data.ChargeDp.DeleteChargeDp}`;
                    await axios.post(url, formFields.ROWID, {
                        headers: {
                            access_token: accessToken,
                            Authorization: `Bearer ${accessToken}`,
                            'Content-Type': 'application/json'
                        }
                    });

                    // This endpoint answers a real status code, so a failure lands in catch below -
                    // no need to inspect the body for Success=false the way the older screens do.
                    // Rule 7: a successful delete is green and returns to the listing.
                    notify(MESSAGES.DELETE_SUCCESS, "success", 3000);
                    setformFields(initialFormFields);
                    setTimeout(() => {
                        history.push(ChargeProfileRoutes.List);
                    }, 1000);
                } catch (error) {
                    if (error?.[CONCURRENCY_HANDLED]) {
                        return;
                    }

                    notify(getApiErrorMessage(error, MESSAGES.ERROR), "error", 5000);
                } finally {
                    hideLoadPanel();
                }
            }
        });
    };

    /**
     * uWMD0008.btnChargesDefaultsClick - opens the Charge Codes screen on this profile's template
     * charge, the WMCHARGES row with R_PROFILECODE = this profile and RECORDTYPE = 'P'.
     *
     * Delphi passed both the profile ROWID and its code to uWMD0001.SetParameters. Same shape as
     * itemDataProfile/Form.js goToItemDefaults: CHARGEDEFAULTSROWID is the existence flag that came
     * back with the record, so an existing template goes straight to the charge code's Edit route and
     * a profile with no template yet goes to the Add route, which seeds the row from RProfile.
     *
     * The id is the CHARGE's RowId, never the profile's - sending the profile key to a GetChargeCode
     * endpoint is the bug the Item Data Profile screen had to fix after the fact. Only the Add route
     * carries RProfile as the seed instruction; the Edit route carries it so Back can find its way
     * home, because the loaded charge already knows its own R_PROFILECODE.
     */
    const goToChargesDefaults = () => {
        if (!isSaved) {
            return;
        }

        const profileCode = encodeURIComponent(formFields.PROFILECODE || '');
        const target = formFields.CHARGEDEFAULTSROWID
            ? `${ChargeCodeRoutes.Edit}?id=${formFields.CHARGEDEFAULTSROWID}&recordType=${RECORD_TYPE.PROFILE_DEFAULTS}&RProfile=${formFields.ROWID}&profileCode=${profileCode}`
            : `${ChargeCodeRoutes.Create}?recordType=${RECORD_TYPE.PROFILE_DEFAULTS}&RProfile=${formFields.ROWID}&profileCode=${profileCode}`;

        history.push(target);
    };

    const addNewRecordClick = () => {
        setformFields(initialFormFields);
    };

    const handleReset = () => {
        const rowId = props.id;
        if (rowId && rowId !== EMPTY_GUID) {
            loadChargeProfileById(rowId);
        } else {
            setformFields(initialFormFields);
        }
    };

    const goBack = () => {
        history.push(ChargeProfileRoutes.List);
    };

    return (
        <div>
            <div className="row dataEntryMainPage">
                <div className="col-md-12 main-page">
                    <div id="accordion">
                        <LoadPanel
                            shadingColor="rgba(0,0,0,0.4)"
                            position={position}
                            onHiding={hideLoadPanel}
                            visible={loadPanelVisible}
                            showIndicator={true}
                            shading={true}
                            showPane={true}
                            hideOnOutsideClick={false}
                        />
                        <form onSubmit={(e) => handleFormSubmit(e)}>
                            <Toolbar className="header">
                                <Item location="before">
                                    <div className="title">
                                        <h6
                                            style={{
                                                color: window._env_.Color2,
                                                marginLeft: '12px',
                                                marginTop: '0px',
                                                marginBottom: 0
                                            }}
                                        >
                                            {ENTITY_LABEL}
                                        </h6>
                                    </div>
                                </Item>
                                {/* The screen has a single tab, so there is no EntityInfoBar: the
                                    Primary tab always shows Record # / Profile / Description itself. */}
                                <Item location="after">
                                    <Button
                                        text="Save"
                                        useSubmitBehavior={true}
                                        type="default"
                                        className="accordianButton"
                                    />
                                </Item>
                                <Item location="after">
                                    {/* Delphi enables this only once the profile exists
                                        (qeWMChargesDPAfterScroll: not AddMode and RecordsExist, with
                                        FormCreate starting it disabled). On a new record there is no
                                        profile to scope the charge code to (rule 5). */}
                                    <Button className="accordianButton"
                                        disabled={!isSaved}
                                        text="Charges Defaults" type="default" onClick={goToChargesDefaults}>
                                    </Button>
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={addNewRecordClick} text="New" type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={DeleteClick} text="Delete" disabled={!isSaved} type="default" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={handleReset} text="Reset" />
                                </Item>
                                <Item location="after">
                                    <Button className="accordianButton" onClick={goBack} text="Back" />
                                </Item>
                            </Toolbar>
                            {/* One tab: uWMD0008 has no tab set at all - every control sits in the top
                                panel, with a blank group box below it. */}
                            <TabPanel deferRendering={props.id ? true : false} showNavButtons={true} height={"auto"}>
                                <TabItem title="Primary">
                                    <PrimaryTab
                                        formFields={formFields}
                                        setformFields={setformFields}
                                        formRefs={formRefs}
                                        handleFieldChange={handleFieldChange}
                                    />
                                </TabItem>
                            </TabPanel>
                        </form>
                    </div>
                </div>
            </div>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(Form);
